import { Autocomplete, createFilterOptions, TextField } from '@mui/material';
import { IncomeAndExpensesLabels } from 'further-ui/labels';
import { forwardRef, useMemo } from 'react';

/** further-ui sets MuiDialog root to z-index 4000; Popper must sit above it. */
const AUTOCOMPLETE_POPPER_Z_INDEX = 4100;

function labelForStoredIncomeExpenseValue(storedValue: string): string {
  for (const [key, label] of Object.entries(IncomeAndExpensesLabels)) {
    if (key === storedValue) {
      return label;
    }
  }
  return storedValue;
}

/** Same pattern as CounterpartySelector: optional `inputValue` marks the synthetic “create” row. */
type IncomeExpenseOption = {
  value: string;
  label: string;
  customActionTypeId?: string;
  inputValue?: string;
};

type Props = {
  options: Array<IncomeExpenseOption>;
  typeLabel: string;
  error?: { message?: string };
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  name?: string;
  disabled?: boolean;
};

const IncomeOrExpenseTypeSelector = forwardRef<HTMLDivElement, Props>(
  (
    { name, disabled, options, typeLabel, error, placeholder, value, onChange },
    ref,
  ) => {
    const filter = createFilterOptions<IncomeExpenseOption>();

    const autocompleteValue = useMemo((): IncomeExpenseOption | undefined => {
      if (value === undefined || value === '') {
        return undefined;
      }
      const found = options.find((o) => o.value === value);
      if (found) {
        return found;
      }
      return { value, label: labelForStoredIncomeExpenseValue(value) };
    }, [value, options]);

    return (
      <Autocomplete
        freeSolo
        disableClearable
        clearOnBlur
        handleHomeEndKeys
        selectOnFocus
        disabled={disabled}
        ref={ref}
        options={options}
        value={autocompleteValue}
        slotProps={{
          popper: {
            style: { zIndex: AUTOCOMPLETE_POPPER_Z_INDEX },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            error={!!error}
            helperText={error?.message?.toString()}
            placeholder={placeholder}
            size="small"
          />
        )}
        getOptionKey={(option) =>
          typeof option === 'string' ? option : option.value
        }
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.label;
        }}
        filterOptions={(optionList, params) => {
          const objectOptions = optionList.filter(
            (o): o is IncomeExpenseOption => typeof o !== 'string',
          );
          const filtered = filter(objectOptions, params);

          const { inputValue } = params;
          const trimmedInputValue = inputValue.trim();

          const isExisting = options.some(
            (o) => o.label.toLowerCase() === trimmedInputValue.toLowerCase(),
          );
          if (trimmedInputValue !== '' && !isExisting) {
            filtered.push({
              value: trimmedInputValue,
              inputValue: trimmedInputValue,
              label: `Add "${trimmedInputValue}" as a new ${typeLabel}`,
            });
          }

          return filtered;
        }}
        renderOption={(props, option) => (
          <li {...props}>
            {typeof option === 'string' ? option : option.label}
          </li>
        )}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            onChange(newValue);
            return;
          }
          if (newValue.inputValue) {
            onChange(newValue.inputValue);
            return;
          }
          onChange(newValue.value);
        }}
      />
    );
  },
);

export default IncomeOrExpenseTypeSelector;
